import { useQuery as useSimilarBrandsQuery } from "@faire/web-api/api/v2/brands/similar/brandToken/get-hooks";
import { trackBrandMarketplaceSimilarBrandsCtaClick } from "@faire/web-api/events/brandMarketplace/click/similarBrandsCta";
import { trackBrandMarketplaceSimilarBrandsTileClick } from "@faire/web-api/events/brandMarketplace/click/similarBrandsTile";
import { IBrand } from "@faire/web-api/indigofair/data/IBrand";
import { IClickInfo } from "@faire/web-api/indigofair/data/IClickInfo";
import { IGetBrandTopCategoryResponse } from "@faire/web-api/indigofair/data/IGetBrandTopCategoryResponse";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Button } from "@faire/web/slate/Button";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React from "react";

import { CarouselWithTitle } from "@faire/retailer/components/Carousel/CarouselWithTitle";
import { ILeanBrandTile } from "@faire/retailer/components/LeanBrandTileRenderer/ILeanBrandTileAdapters";
import { LeanBrandTileRendererForCarousel } from "@faire/retailer/components/LeanBrandTileRenderer/LeanBrandTileRendererForCarousel";
import { TrackedLinkContext } from "@faire/retailer/components/TrackedLink";
import { categoryPath } from "@faire/retailer/paths/category/categoryPath";

import { CarouselContainer, Wrapper } from "./common";

interface IProps {
  brandToken: string;
  topCategory?: IGetBrandTopCategoryResponse;
  brandName?: string;
  className?: string;
}

const MINIMUM_CAROUSEL_TILES_TO_DISPLAY = 5;

export const SimilarBrandsCarousel = React.forwardRef<HTMLDivElement, IProps>(
  ({ brandToken, topCategory, brandName, className }, ref) => {
    const { strictLocalize } = useStrictLocalization();
    const { data: similarBrandsData } = useSimilarBrandsQuery(brandToken);

    const { isMobile, isTablet } = useViewport();

    if (
      !similarBrandsData?.brands ||
      similarBrandsData.brands.length < MINIMUM_CAROUSEL_TILES_TO_DISPLAY
    ) {
      return null;
    }

    const renderBrandTile = (brand: IBrand, index: number) => {
      return (
        <LeanBrandTileRendererForCarousel
          key={index}
          leanBrandTile={ILeanBrandTile.fromIBrand(brand)}
          onClick={() => {
            trackBrandMarketplaceSimilarBrandsTileClick();
          }}
          signUpSourceId="similar_brands_carousel"
        />
      );
    };

    const title =
      !isMobile && brandName
        ? strictLocalize(
            { defaultMessage: "More brands like {brandCompanyName}" },
            { brandCompanyName: brandName }
          )
        : strictLocalize({ defaultMessage: "More brands like this" });

    const cta = topCategory?.category_url_id ? (
      <Button
        variant="tertiary"
        linkTo={categoryPath({
          urlIds: [
            { id: topCategory.category_url_id },
            ...(topCategory.subcategory_url_id?.split("|")?.map((name) => {
              return { id: name };
            }) ?? []),
          ],
        })}
        onClick={() => {
          trackBrandMarketplaceSimilarBrandsCtaClick();
        }}
      >
        <StrictLocalMsg
          defaultMessage="Shop all"
          description={{
            text: "Button navigating to a list with all items of a given type",
          }}
        />
      </Button>
    ) : undefined;

    return (
      <Wrapper className={className} ref={ref}>
        <CarouselContainer>
          <TrackedLinkContext.Provider
            value={{
              type: IClickInfo.Type.BRAND,
              containerName: "logged_out_brand_similar_brands",
            }}
          >
            <CarouselWithTitle
              name="logged_out_brand_similar_brands"
              title={title}
              titleRight={cta}
              // eslint-disable-next-line no-nested-ternary
              itemsPerSlide={isMobile ? 2 : isTablet ? 4 : 5}
              items={similarBrandsData.brands}
              renderItem={renderBrandTile}
              itemGap={8}
              padItems
              peekNext={isMobile}
              peekGapPercent={20}
            />
          </TrackedLinkContext.Provider>
        </CarouselContainer>
      </Wrapper>
    );
  }
);
